import React from "react";
import { CheckCircle, ShieldCheck, MapPin, Clock } from "lucide-react";
import Seo from "../components/Seo";
import EnteteLanding from "../components/EnteteLanding";
import PiedLanding from "../components/PiedLanding";
import SimulateurRapide from "../components/SimulateurRapide";
import BoutonRappel from "../components/BoutonRappel";
import { serviceJsonLd, DEPARTEMENTS_COUVERTS } from "../utils/donneesStructurees";
import { formaterEuros, PLAFOND_TRAVAUX } from "../utils/plafonds";

/**
 * Page d'atterrissage Google Ads — monte-escalier.
 *
 * En noindex et hors sitemap, pour les mêmes raisons que la page salle de
 * bain : /monte-escalier existe déjà et travaille l'organique sur ces mots.
 */

// Types d'appareils et fourchettes de prix couramment pratiquées sur le
// marché. Ce sont des ordres de grandeur destinés à situer le reste à charge,
// pas des tarifs que nous pratiquons — nous ne vendons pas les appareils.
const TYPES = [
  {
    nom: "Monte-escalier droit",
    prix: "3 000 à 5 000 €",
    texte:
      "Escalier d'une seule volée, sans palier ni virage. Pose en une demi-journée, rail fixé sur les marches et non sur le mur.",
  },
  {
    nom: "Monte-escalier courbe",
    prix: "8 000 à 12 000 €",
    texte:
      "Escalier avec virage, palier intermédiaire ou colimaçon. Rail fabriqué sur mesure après relevé, délai de trois à six semaines.",
  },
  {
    nom: "Plateforme élévatrice",
    prix: "9 000 à 15 000 €",
    texte:
      "Pour un fauteuil roulant, à l'intérieur ou pour franchir quelques marches à l'extérieur. Nécessite une largeur d'escalier suffisante.",
  },
  {
    nom: "Monte-escalier extérieur",
    prix: "5 000 à 9 000 €",
    texte:
      "Perron ou accès au jardin. Rail et moteur traités contre les intempéries, alimentation protégée.",
  },
];

// Cas détaillé : un monte-escalier courbe à 8 000 €, ménage très modeste.
const CAS = { travaux: 8000, taux: 0.7 };
const CAS_AIDE = Math.round(CAS.travaux * CAS.taux);

const LandingMonteEscalier = () => (
  <div className="min-h-screen bg-white">
    <Seo
      titre="Monte-escalier MaPrimeAdapt' Île-de-France | Financement jusqu'à 70%"
      description="Monte-escalier droit, courbe ou plateforme élévatrice : MaPrimeAdapt' finance jusqu'à 70 % de l'installation en Île-de-France. Vérifiez votre éligibilité en 30 secondes, AMO agréé ANAH."
      chemin="/monte-escalier-aide-anah-idf"
      image="/images/monte-escalier.webp"
      noindex
      donnees={[
        serviceJsonLd({
          nom: "Financement d'un monte-escalier avec MaPrimeAdapt'",
          description:
            "Montage du dossier MaPrimeAdapt' pour l'installation d'un monte-escalier droit, courbe ou d'une plateforme élévatrice en Île-de-France.",
          url: "/monte-escalier-aide-anah-idf",
        }),
      ]}
    />
    <EnteteLanding />

    <main id="contenu">
      <section className="bg-gradient-to-br from-blue-50 to-white py-10 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start max-w-6xl mx-auto">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-blue-700 text-white rounded-full text-sm font-medium">
                <ShieldCheck className="h-4 w-4 mr-2" aria-hidden="true" />
                AMO agréé ANAH — Île-de-France
              </div>

              <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 leading-tight">
                {/* Espace explicite : le span est en block, l'espace ne se voit
                    pas, mais sans lui le H1 lu donne « monte-escalierà ». */}
                Financez votre monte-escalier{" "}
                <span className="text-blue-700 block mt-2">à 70 % avec MaPrimeAdapt&apos;</span>
              </h1>

              <p className="text-lg lg:text-xl text-gray-900 leading-relaxed">
                Un monte-escalier est un travail d&apos;adaptation éligible à
                MaPrimeAdapt&apos;. Selon vos ressources, l&apos;État prend en charge{" "}
                <strong>50 % ou 70 % du montant</strong>. Nous montons le dossier
                auprès de l&apos;ANAH à votre place, et{" "}
                <strong>notre accompagnement est gratuit</strong>.
              </p>

              <img
                src="/images/monte-escalier.webp"
                alt="Monte-escalier installé le long d'un escalier de pavillon"
                width="900"
                height="600"
                loading="eager"
                className="w-full rounded-xl shadow-lg object-cover h-56 lg:h-72"
              />

              <div className="flex flex-col sm:flex-row gap-4">
                <BoutonRappel
                  libelle="Être rappelé gratuitement"
                  variant="default"
                  className="bg-blue-700 hover:bg-blue-800 text-white text-lg min-h-[56px] w-full sm:w-auto"
                />
                <a
                  href="tel:0939035444"
                  className="inline-flex items-center justify-center gap-2 border-2 border-blue-700 text-blue-800 hover:bg-blue-50 rounded-lg px-6 text-lg font-semibold min-h-[56px] w-full sm:w-auto"
                >
                  09 39 03 54 44
                </a>
              </div>
            </div>

            <SimulateurRapide travauxEstimes={CAS.travaux} />
          </div>
        </div>
      </section>

      {/* Exemple de financement détaillé */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">
              Un monte-escalier courbe à {formaterEuros(CAS.travaux)}
            </h2>
            <p className="text-xl text-gray-700 mb-10 text-center">
              Pour un ménage très modeste en Île-de-France, pris en charge à{" "}
              {Math.round(CAS.taux * 100)} %.
            </p>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 sm:p-8 space-y-4">
              <div className="flex justify-between items-center text-lg text-gray-900">
                <span>Installation du monte-escalier</span>
                <span className="font-semibold">{formaterEuros(CAS.travaux)}</span>
              </div>
              <div className="flex justify-between items-center text-lg text-green-800 font-bold">
                <span>Aide MaPrimeAdapt&apos; ({Math.round(CAS.taux * 100)} %)</span>
                <span>− {formaterEuros(CAS_AIDE)}</span>
              </div>
              <div className="flex justify-between items-center text-2xl text-gray-900 font-bold pt-4 border-t-2 border-blue-200">
                <span>Reste à votre charge</span>
                <span>{formaterEuros(CAS.travaux - CAS_AIDE)}</span>
              </div>
            </div>

            <p className="text-gray-700 mt-6">
              Le même appareil chez un ménage modeste est pris en charge à 50 %, soit{" "}
              {formaterEuros(Math.round(CAS.travaux * 0.5))} d&apos;aide et{" "}
              {formaterEuros(CAS.travaux - Math.round(CAS.travaux * 0.5))} de reste à
              charge. Au-delà des plafonds de ressources, MaPrimeAdapt&apos; ne
              s&apos;applique pas, mais la TVA reste à 5,5 % et votre caisse de retraite
              peut intervenir.
            </p>

            <p className="text-sm text-gray-700 mt-4">
              L&apos;aide porte sur le montant des travaux dans la limite de{" "}
              {formaterEuros(PLAFOND_TRAVAUX)} HT, tous travaux d&apos;adaptation
              confondus : si vous adaptez aussi la salle de bain, le plafond est commun.
            </p>

            <div className="text-center mt-10">
              <BoutonRappel
                libelle="Estimer mon reste à charge"
                variant="default"
                className="bg-blue-700 hover:bg-blue-800 text-white text-lg min-h-[56px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Types d'appareils */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Quel appareil pour quel escalier ?
              </h2>
              <p className="text-xl text-gray-700">
                Le prix dépend surtout de la forme de l&apos;escalier. Un rail courbe
                est fabriqué sur mesure, c&apos;est ce qui explique l&apos;écart.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {TYPES.map((t) => (
                <div key={t.nom} className="bg-white rounded-xl border-2 border-gray-200 p-6">
                  <div className="flex items-baseline justify-between gap-4 mb-3">
                    <h3 className="text-xl font-bold text-gray-900">{t.nom}</h3>
                    <span className="text-blue-800 font-bold whitespace-nowrap">{t.prix}</span>
                  </div>
                  <p className="text-gray-700">{t.texte}</p>
                </div>
              ))}
            </div>

            <p className="text-sm text-gray-700 mt-8 text-center max-w-3xl mx-auto">
              Fourchettes de prix couramment constatées sur le marché, données pour
              situer le reste à charge. Nous ne vendons pas d&apos;appareil : le devis
              vient de l&apos;installateur, et nous vérifions qu&apos;il est conforme au
              dossier ANAH.
            </p>
          </div>
        </div>
      </section>

      {/* Zones d'intervention */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Où nous intervenons
            </h2>
            <p className="text-xl text-gray-700 mb-10">
              Nous sommes habilités par l&apos;ANAH dans six départements franciliens.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {DEPARTEMENTS_COUVERTS.map((d) => (
                <div
                  key={d.code}
                  className="flex items-center justify-center gap-2 bg-blue-50 border-2 border-blue-100 rounded-lg py-4 px-3"
                >
                  <MapPin className="h-5 w-5 text-blue-700 flex-shrink-0" aria-hidden="true" />
                  <span className="font-semibold text-gray-900">
                    {d.code} {d.nom}
                  </span>
                </div>
              ))}
            </div>

            <p className="text-gray-700 mt-8">
              Votre département n&apos;est pas dans la liste ? Appelez-nous : nos
              habilitations s&apos;étendent, et nous pouvons vous orienter vers un AMO
              agréé près de chez vous.
            </p>
          </div>
        </div>
      </section>

      {/* Appel à l'action final */}
      <section className="py-16 lg:py-24 bg-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Parlons de votre escalier
            </h2>
            <p className="text-xl text-blue-50 mb-10 max-w-2xl mx-auto">
              Quelques minutes au téléphone suffisent à savoir si le dossier tient :
              vos ressources, la configuration de l&apos;escalier, et si vous êtes
              propriétaire du logement.
            </p>

            <ul className="grid sm:grid-cols-3 gap-4 text-left mb-10">
              {[
                "Diagnostic à domicile gratuit",
                "Dossier ANAH monté par nos soins",
                "Aucun engagement de votre part",
              ].map((point) => (
                <li key={point} className="flex items-start gap-3 bg-blue-800 rounded-lg p-4">
                  <CheckCircle className="h-6 w-6 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <BoutonRappel
                libelle="Choisir un créneau de rappel"
                variant="default"
                className="bg-white text-blue-800 hover:bg-blue-50 text-lg min-h-[56px] w-full sm:w-auto"
              />
              <a
                href="tel:0939035444"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-blue-800 rounded-lg px-6 text-lg font-semibold min-h-[56px] w-full sm:w-auto"
              >
                09 39 03 54 44
              </a>
            </div>

            <p className="text-blue-100 mt-6 flex items-center justify-center gap-2">
              <Clock className="h-5 w-5" aria-hidden="true" />
              Du lundi au jeudi 10 h - 17 h, vendredi 10 h - 13 h
            </p>
          </div>
        </div>
      </section>
    </main>

    <PiedLanding />
  </div>
);

export default LandingMonteEscalier;
